import { useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { SEO } from "../components/SEO";
import { FieldError } from "../components/site/FieldError";
import { validators, runValidators } from "../lib/validation";
import { toast } from "sonner";

export function AboutPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 md:py-24" data-testid="about-page">
      <SEO title="About" path="/about" description="BookMala is India's magazine subscription store - connecting readers with the country's finest periodicals since 2026." />
      <span className="eyebrow">Our Story</span>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 text-bm-ink">About BookMala</h1>
      <div className="mt-10 space-y-6 text-bm-body leading-relaxed text-lg">
        <p>BookMala began with a simple observation: India's magazines are extraordinary, but subscribing to them is stuck in another era - paper forms, money orders, and missed issues.</p>
        <p>We partner directly with publishing houses across the country to bring their periodicals to one warm, trustworthy storefront. Every subscription includes doorstep delivery of the print edition and instant access to a beautiful digital flip edition.</p>
        <p>From business weeklies to devotional monthlies, from kids' comics to regional literature - we believe every Indian home deserves a magazine on its table.</p>
      </div>
    </div>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const fields = () => ({
    name: [form.name, [validators.required]],
    email: [form.email, [validators.email]],
    message: [form.message, [validators.minLength(5, "Message")]],
  });
  const blur = (k) => {
    const [v, fns] = fields()[k];
    setErrors((e) => ({ ...e, [k]: fns.map((f) => f(v)).find(Boolean) || "" }));
  };
  const submit = async (e) => {
    e.preventDefault();
    const { errors: errs, hasError } = runValidators(fields());
    setErrors(errs);
    if (hasError) return;
    setBusy(true);
    try {
      await api.post("/contact", { name: form.name.trim(), email: form.email.trim().toLowerCase(), message: form.message.trim() });
      setSent(true);
      toast.success("Message sent! We'll reply within one working day.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12" data-testid="contact-page">
      <SEO title="Contact" path="/contact" />
      <div>
        <span className="eyebrow">Get In Touch</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 text-bm-ink">Talk to us</h1>
        <p className="text-bm-body mt-4 leading-relaxed">Questions about a subscription, a missing issue, or partnering as a publisher? We reply within one working day.</p>
        <div className="mt-10 space-y-4 text-sm text-bm-body">
          <p className="flex items-center gap-3"><Mail className="w-4 h-4 text-bm-terra" /> care@bookmala.in</p>
          <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-bm-terra" /> +91 98765 00000</p>
          <p className="flex items-center gap-3"><MapPin className="w-4 h-4 text-bm-terra" /> Connaught Place, New Delhi 110001</p>
        </div>
      </div>
      {sent ? (
        <div className="bg-bm-sand rounded-xl p-10 text-center self-center" data-testid="contact-success">
          <h3 className="font-display font-semibold text-2xl text-bm-ink">Thank you!</h3>
          <p className="text-sm text-bm-body mt-2">Your message is on its way to our team.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="contact-form" noValidate>
          {[["name", "Your name", "text"], ["email", "Email address", "email"]].map(([k, label, type]) => (
            <div key={k}>
              <input type={type} placeholder={label} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                onBlur={() => blur(k)} aria-invalid={!!errors[k]}
                data-testid={`contact-${k}-input`} className={`bm-input ${errors[k] ? "!border-red-400" : ""}`} />
              <FieldError msg={errors[k]} id={`contact-${k}`} />
            </div>
          ))}
          <div>
            <textarea rows={5} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              onBlur={() => blur("message")} aria-invalid={!!errors.message}
              data-testid="contact-message-input" className={`bm-input ${errors.message ? "!border-red-400" : ""}`} />
            <FieldError msg={errors.message} id="contact-message" />
          </div>
          <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="btn-primary">
            {busy ? "Sending..." : "Send Message"}
          </button>
        </form>
      )}
    </div>
  );
}
